import React, { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { PageHeader, LoadingSkeleton, EmptyState } from "@/components/shared/States";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import {
  Plug, Check, Calendar, Mail, Video, MessageSquare, CreditCard, FileText,
  ShoppingCart, Zap, ArrowRight, KeyRound, ListChecks, Sparkles, ExternalLink,
  Search, Flame, X, Copy, Link2, HardDrive, Image as ImageIcon, Bell,
} from "lucide-react";
import { toast } from "sonner";

// Most-used integrations get a subtle "Popular" tag.
const POPULAR = new Set(["google_workspace", "slack", "stripe", "zoom", "zapier", "shopify", "perplexity", "sendgrid", "google_signin"]);

// Official vendor branding. `si` = Simple Icons slug (crisp coloured SVG),
// `domain` = fallback favicon via DuckDuckGo, `Icon` = final lucide fallback.
const VENDORS = {
  google_workspace: { si: "google", domain: "google.com", Icon: Calendar },
  microsoft365: { si: null, domain: "office.com", Icon: Mail },
  slack: { si: null, domain: "slack.com", Icon: MessageSquare },
  zoom: { si: "zoom", domain: "zoom.us", Icon: Video },
  teams: { si: null, domain: "teams.microsoft.com", Icon: Video },
  stripe: { si: "stripe", domain: "stripe.com", Icon: CreditCard },
  twilio: { si: null, domain: "twilio.com", Icon: MessageSquare },
  quickbooks: { si: "quickbooks", domain: "quickbooks.intuit.com", Icon: FileText },
  xero: { si: "xero", domain: "xero.com", Icon: FileText },
  docusign: { si: null, domain: "docusign.com", Icon: FileText },
  shopify: { si: "shopify", domain: "shopify.com", Icon: ShoppingCart },
  woocommerce: { si: "woocommerce", domain: "woocommerce.com", Icon: ShoppingCart },
  wordpress: { si: "wordpress", domain: "wordpress.org", Icon: FileText },
  zapier: { si: "zapier", domain: "zapier.com", Icon: Zap },
  make: { si: "make", domain: "make.com", Icon: Zap },
  perplexity: { si: "perplexity", domain: "perplexity.ai", Icon: Search },
  sendgrid: { si: "twilio", domain: "sendgrid.com", Icon: Mail },
  emergent_email: { si: null, domain: null, Icon: Mail },
  google_signin: { si: "google", domain: "google.com", Icon: KeyRound },
  object_storage: { si: null, domain: null, Icon: HardDrive },
  ai_image_generation: { si: null, domain: null, Icon: ImageIcon },
  push_notifications: { si: null, domain: null, Icon: Bell },
};

// Per-vendor setup guides. Anything missing falls back to GENERIC_GUIDE.
const SETUP_GUIDES = {
  google_workspace: {
    capabilities: ["Two-way Gmail sync", "Calendar & Meet scheduling", "Drive & Docs attachments", "Contacts enrichment"],
    prerequisites: ["A Google Cloud project with OAuth consent screen configured", "GOOGLE_CLIENT_ID & GOOGLE_CLIENT_SECRET added to the backend"],
    steps: [
      "In Google Cloud Console, create OAuth 2.0 credentials (Web application).",
      "Add Aurelo's redirect URI to the authorised redirect URIs list.",
      "Copy the Client ID and Client Secret into the backend environment.",
      "Click Connect below and approve the requested Gmail & Calendar scopes.",
    ],
    docs: "https://developers.google.com/workspace",
  },
  microsoft365: {
    capabilities: ["Outlook email sync", "Calendar scheduling", "Teams meeting links"],
    prerequisites: ["Microsoft Entra (Azure AD) app registration", "Client ID & secret with Mail/Calendar permissions"],
    steps: [
      "Register an application in the Microsoft Entra admin center.",
      "Grant Mail.ReadWrite and Calendars.ReadWrite delegated permissions.",
      "Create a client secret and add the redirect URI.",
      "Enter the credentials in Aurelo and connect.",
    ],
    docs: "https://learn.microsoft.com/graph",
  },
  slack: {
    capabilities: ["Deal & lead alerts", "Channel notifications", "Slash-command lookups"],
    prerequisites: ["A Slack workspace where you can install apps", "Bot token with chat:write scope"],
    steps: [
      "Create a Slack app at api.slack.com/apps.",
      "Add the chat:write and channels:read OAuth scopes.",
      "Install the app to your workspace and copy the Bot token.",
      "Paste the token in Aurelo and choose your alert channel.",
    ],
    docs: "https://api.slack.com/start",
  },
  zoom: {
    capabilities: ["Create Zoom meetings from deals", "Auto-attach join links", "Meeting reminders"],
    prerequisites: ["A Zoom account", "A Server-to-Server OAuth app"],
    steps: [
      "In the Zoom App Marketplace, create a Server-to-Server OAuth app.",
      "Enable meeting:write scopes.",
      "Copy the Account ID, Client ID and Client Secret.",
      "Add them to Aurelo and connect.",
    ],
    docs: "https://developers.zoom.us",
  },
  stripe: {
    capabilities: ["Billing & invoice sync", "Revenue reporting", "Subscription status on contacts"],
    prerequisites: ["A Stripe account", "A restricted or secret API key"],
    steps: [
      "Open the Stripe Dashboard → Developers → API keys.",
      "Create a restricted key with read access to charges & customers.",
      "Paste the key into Aurelo.",
      "Connect and map Stripe customers to Aurelo contacts.",
    ],
    docs: "https://stripe.com/docs/api",
  },
  twilio: {
    capabilities: ["SMS from CRM", "Voice calling", "Delivery status logging"],
    prerequisites: ["A Twilio account", "Account SID, Auth token & a phone number"],
    steps: [
      "Find your Account SID and Auth token in the Twilio Console.",
      "Buy or select an SMS-capable phone number.",
      "Enter the SID, token and number in Aurelo.",
      "Connect and send a test message.",
    ],
    docs: "https://www.twilio.com/docs",
  },
  docusign: {
    capabilities: ["Send quotes for e-signature", "Track signature status", "Store signed docs on deals"],
    prerequisites: ["A DocuSign developer/production account", "An integration key (client ID)"],
    steps: [
      "Create an app in the DocuSign Admin / Apps and Keys page.",
      "Generate an integration key and RSA keypair or secret.",
      "Add the redirect URI and credentials to Aurelo.",
      "Connect and authorise account access.",
    ],
    docs: "https://developers.docusign.com",
  },
  shopify: {
    capabilities: ["Sync customers & orders", "Order-value on contacts", "Abandoned-cart signals"],
    prerequisites: ["A Shopify store", "A custom app with Admin API access"],
    steps: [
      "In Shopify admin, create a custom app under Settings → Apps.",
      "Grant read access to customers and orders.",
      "Install the app and copy the Admin API access token.",
      "Add your store domain and token to Aurelo.",
    ],
    docs: "https://shopify.dev/docs/api",
  },
  zapier: {
    capabilities: ["Connect 5000+ apps", "No-code workflows", "Trigger on Aurelo events"],
    prerequisites: ["A Zapier account", "Your Aurelo API key"],
    steps: [
      "Create a new Zap in Zapier.",
      "Search for the Aurelo app and add it as a trigger or action.",
      "Authenticate using your Aurelo API key.",
      "Map fields and turn the Zap on.",
    ],
    docs: "https://zapier.com/apps",
  },
  make: {
    capabilities: ["Visual automation scenarios", "Multi-step routing", "Event-driven syncs"],
    prerequisites: ["A Make account", "Your Aurelo API key"],
    steps: [
      "Create a new scenario in Make.",
      "Add the Aurelo module and connect with your API key.",
      "Build your scenario logic.",
      "Schedule or trigger the scenario.",
    ],
    docs: "https://www.make.com/en/help",
  },
  perplexity: {
    capabilities: ["Real-time web search for the Growth Engine's lead finding", "Public-source account research (the 'LinkedIn inspect' step)", "Jev-scored candidate leads from live search results"],
    prerequisites: ["A Perplexity API account", "An API key with Search access"],
    steps: [
      "This is already connected using a securely stored server-side API key — no action needed.",
      "Used automatically whenever you click 'Find Leads' or 'Research' in the Growth Engine.",
      "Results always include the public source URL so you can verify before outreach.",
      "To rotate the key, update it from Settings → Secrets.",
    ],
    docs: "https://docs.perplexity.ai",
  },
  sendgrid: {
    capabilities: ["Sends real outreach emails from approved Growth Engine campaigns", "Transactional email delivery", "Delivery status tracking"],
    prerequisites: ["A SendGrid account", "A verified Sender Identity (single sender or domain)"],
    steps: [
      "This is already connected using a securely stored server-side API key — no action needed.",
      "IMPORTANT: verify your sender address in SendGrid → Settings → Sender Authentication before sending — unverified senders are rejected.",
      "Used automatically when you send a test email or launch an approved Growth Engine sequence.",
      "To rotate the key or sender address, update it from Settings → Secrets.",
    ],
    docs: "https://docs.sendgrid.com",
  },
  emergent_email: {
    capabilities: ["Sends team invite emails instantly", "No sender verification needed", "Delivered from Aurelo's managed domain"],
    prerequisites: ["None — this is fully configured out of the box"],
    steps: [
      "This is already connected and fully managed — no setup required.",
      "Used automatically whenever you invite a teammate from Admin → Users.",
      "If an invite email can't be delivered, a direct invite link is always shown as a fallback.",
    ],
    docs: null,
  },
  google_signin: {
    capabilities: ["One-click sign in or sign up with Google", "No separate password to remember", "Automatically links to an existing Aurelo account with the same email"],
    prerequisites: ["None — this is fully configured out of the box"],
    steps: [
      "This is already connected — no setup required.",
      "Click \"Continue with Google\" on the Login or Sign Up page to use it.",
      "@210codelabs.com accounts are automatically granted platform admin access.",
    ],
    docs: null,
  },
  object_storage: {
    capabilities: ["Stores profile photos securely", "Stores AI-generated images", "Serves images with fast, reliable URLs"],
    prerequisites: ["None — this is fully configured out of the box"],
    steps: [
      "This is already connected — no setup required.",
      "Used automatically when you upload or generate a profile picture from My Account.",
    ],
    docs: null,
  },
  ai_image_generation: {
    capabilities: ["Generate a profile picture from a text prompt", "Powered by Google's image generation models", "Results are saved directly to your profile"],
    prerequisites: ["None — this is fully configured out of the box"],
    steps: [
      "This is already connected — no setup required.",
      "Go to My Account → Profile and click \"Generate with AI\".",
      "Describe the image you want and Aurelo creates it in seconds.",
    ],
    docs: null,
  },
  push_notifications: {
    capabilities: ["Real browser alerts when a teammate joins", "Works even when the Aurelo tab isn't focused", "No external account required"],
    prerequisites: ["A browser that supports push notifications (Chrome, Edge, Firefox)"],
    steps: [
      "Go to My Account → Notifications.",
      "Click \"Turn on browser notifications\" and allow the permission prompt.",
      "Send yourself a test notification to confirm it's working.",
    ],
    docs: null,
  },
};

const GENERIC_GUIDE = {
  capabilities: ["Sync records with Aurelo", "Keep data up to date automatically", "Reduce manual entry"],
  prerequisites: ["An active account with this provider", "API credentials or admin access to authorise the connection"],
  steps: [
    "Obtain API credentials from the provider's developer settings.",
    "Enter the credentials in Aurelo.",
    "Click Connect below to authorise the integration.",
    "Confirm the connection and configure sync preferences.",
  ],
  docs: null,
};

const VendorLogo = ({ vkey, name, size = "h-7 w-7" }) => {
  const v = VENDORS[vkey] || {};
  const sources = [];
  if (v.si) sources.push(`https://cdn.simpleicons.org/${v.si}`);
  if (v.domain) sources.push(`https://icons.duckduckgo.com/ip3/${v.domain}.ico`);
  const [i, setI] = useState(0);
  if (sources.length && i < sources.length) {
    return (
      <img
        src={sources[i]}
        alt={`${name} logo`}
        onError={() => setI((n) => n + 1)}
        className={`${size} object-contain`}
        loading="lazy"
      />
    );
  }
  const Icon = v.Icon || Plug;
  return <Icon className="h-5 w-5 text-[#7C3AED]" />;
};

const STATUS_STYLE = {
  "Connected": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Configuration Required": "bg-amber-50 text-amber-700 border-amber-200",
  "Not Connected": "bg-slate-100 text-slate-500 border-slate-200",
};

export default function Integrations() {
  const [items, setItems] = useState(null);
  const [active, setActive] = useState(null); // integration currently shown in the panel
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");
  const [copied, setCopied] = useState(false);

  const redirectUri = `${API}/oauth/callback`;
  const copyRedirectUri = async () => {
    const legacyCopy = () => {
      const ta = document.createElement("textarea");
      ta.value = redirectUri;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      if (!ok) throw new Error("execCommand copy failed");
    };
    try {
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(redirectUri);
        } else {
          legacyCopy();
        }
      } catch {
        legacyCopy(); // Clipboard API present but blocked (permissions) -> fall back
      }
      setCopied(true);
      toast.success("Redirect URI copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — please copy it manually.");
    }
  };

  const load = async () => { const { data } = await api.get("/integrations"); setItems(data.items); };

  useEffect(() => {
    load();
    const p = new URLSearchParams(window.location.search);
    if (p.get("google") === "connected") toast.success("Google Workspace connected!");
    if (p.get("google") === "error") toast.error("Google connection was cancelled or failed.");
  }, []);

  const openGuide = (it) => { setActive(it); setOpen(true); };

  const connect = async (key) => {
    if (key === "google_workspace") {
      const token = localStorage.getItem("aurelo_token");
      const tenant = localStorage.getItem("aurelo_tenant");
      try {
        const { data } = await api.get("/google/status");
        if (!data.configured) { toast.error("Google not configured", { description: "Add GOOGLE_CLIENT_ID & GOOGLE_CLIENT_SECRET to the backend, then reconnect." }); return; }
      } catch {}
      window.location.href = `${API}/oauth/start?feature=all&token=${token}&tenant_id=${tenant}`;
      return;
    }
    if (["emergent_email", "google_signin", "object_storage", "ai_image_generation"].includes(key)) {
      toast.info("This is already fully configured — nothing to connect.");
      return;
    }
    if (key === "push_notifications") {
      toast.info("Enable this from My Account → Notifications.");
      return;
    }
    const { data } = await api.post(`/integrations/${key}/connect`);
    toast(data.message || "Configuration required", { description: "Provide provider credentials to finish connecting." });
    await load();
    // keep the panel status in sync
    setActive((cur) => (cur && cur.key === key ? { ...cur, status: "Configuration Required" } : cur));
  };

  const btnLabel = (status) =>
    status === "Connected" ? "Connected" : status === "Configuration Required" ? "Configure" : "Connect";

  if (!items) return <LoadingSkeleton />;

  const guide = active ? (SETUP_GUIDES[active.key] || GENERIC_GUIDE) : GENERIC_GUIDE;

  const categories = ["All", ...Array.from(new Set(items.map((i) => i.category))).sort()];
  const q = query.trim().toLowerCase();
  const filtered = items.filter((it) => {
    const matchesCat = category === "All" || it.category === category;
    const matchesQuery =
      !q ||
      it.name.toLowerCase().includes(q) ||
      (it.description || "").toLowerCase().includes(q) ||
      (it.category || "").toLowerCase().includes(q);
    return matchesCat && matchesQuery;
  });

  return (
    <div>
      <PageHeader title="Integrations" subtitle="Connect Aurelo to your favorite tools" icon={Plug} />

      <div className="mb-5 space-y-3">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            data-testid="integration-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search integrations..."
            className="pl-9 pr-9"
          />
          {query && (
            <button
              data-testid="integration-search-clear"
              onClick={() => setQuery("")}
              aria-label="Clear search"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <div data-testid="integration-category-filter" className="flex flex-wrap gap-2">
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`integration-category-${c.toLowerCase()}`}
              onClick={() => setCategory(c)}
              className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors duration-200 ${
                category === c
                  ? "aurelo-gradient text-white border-transparent"
                  : "bg-white text-slate-600 border-slate-200 hover:border-[#7C3AED]/40 hover:text-[#7C3AED]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          icon={Search}
          title="No integrations found"
          description={`Nothing matches "${query || category}". Try a different search or category.`}
          actionLabel="Clear filters"
          action={() => { setQuery(""); setCategory("All"); }}
        />
      ) : (
      <div data-testid="integrations-grid" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map((it) => (
          <div
            key={it.key}
            data-testid="integration-card"
            role="button"
            tabIndex={0}
            onClick={() => openGuide(it)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openGuide(it); } }}
            className={`group cursor-pointer rounded-2xl bg-white border p-5 transition-shadow duration-200 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7C3AED]/40 ${it.featured ? "border-[#7C3AED]/40 ring-1 ring-[#7C3AED]/10" : "border-slate-200"}`}
          >
            <div className="flex items-start justify-between">
              <div className="h-11 w-11 rounded-xl bg-white border border-slate-200 shadow-sm flex items-center justify-center overflow-hidden">
                <VendorLogo vkey={it.key} name={it.name} />
              </div>
              <div className="flex items-center gap-1.5">
                {POPULAR.has(it.key) && (
                  <span data-testid="integration-popular-badge" className="inline-flex items-center gap-0.5 text-[11px] font-semibold rounded-full border border-amber-200 bg-amber-50 text-amber-700 px-2 py-0.5">
                    <Flame className="h-3 w-3" /> Popular
                  </span>
                )}
                <span className={`text-[11px] font-semibold rounded-full border px-2 py-0.5 ${STATUS_STYLE[it.status]}`}>{it.status}</span>
              </div>
            </div>
            <div className="mt-3 font-semibold text-slate-900">{it.name}{it.featured && <span className="ml-2 text-[10px] font-bold text-white aurelo-gradient rounded px-1.5 py-0.5">Featured</span>}</div>
            <p className="text-xs text-slate-500 mt-1 h-8">{it.description}</p>
            <div className="mt-3 flex items-center justify-between">
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#7C3AED] group-hover:gap-1.5 transition-[gap] duration-200" data-testid="integration-view-guide">
                Setup guide <ArrowRight className="h-3.5 w-3.5" />
              </span>
              <Button
                data-testid="integration-connect-button"
                size="sm"
                variant={it.status === "Not Connected" ? "outline" : "secondary"}
                disabled={it.status === "Connected"}
                onClick={(e) => { e.stopPropagation(); connect(it.key); }}
              >
                {it.status === "Connected" ? <><Check className="h-4 w-4 mr-1" />Connected</> : btnLabel(it.status)}
              </Button>
            </div>
          </div>
        ))}
      </div>
      )}

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent data-testid="integration-guide-panel" className="w-full sm:max-w-md overflow-y-auto">
          {active && (
            <>
              <SheetHeader>
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-xl bg-white border border-slate-200 shadow-sm flex items-center justify-center overflow-hidden shrink-0">
                    <VendorLogo vkey={active.key} name={active.name} size="h-8 w-8" />
                  </div>
                  <div className="min-w-0">
                    <SheetTitle data-testid="guide-title" className="truncate">{active.name}</SheetTitle>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <Badge variant="outline" className="text-[10px]">{active.category}</Badge>
                      {POPULAR.has(active.key) && (
                        <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold rounded-full border border-amber-200 bg-amber-50 text-amber-700 px-2 py-0.5">
                          <Flame className="h-3 w-3" /> Popular
                        </span>
                      )}
                      <span className={`text-[11px] font-semibold rounded-full border px-2 py-0.5 ${STATUS_STYLE[active.status]}`} data-testid="guide-status">{active.status}</span>
                    </div>
                  </div>
                </div>
                <SheetDescription className="pt-3 text-left">{active.description}</SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-6">
                <section data-testid="guide-capabilities">
                  <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                    <Sparkles className="h-4 w-4 text-[#7C3AED]" /> What you can do
                  </h3>
                  <ul className="mt-2 space-y-1.5">
                    {guide.capabilities.map((c, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                        <Check className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" /> {c}
                      </li>
                    ))}
                  </ul>
                </section>

                <Separator />

                <section data-testid="guide-prerequisites">
                  <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                    <KeyRound className="h-4 w-4 text-[#7C3AED]" /> Before you start
                  </h3>
                  <ul className="mt-2 space-y-1.5">
                    {guide.prerequisites.map((p, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                        <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-slate-300 shrink-0" /> {p}
                      </li>
                    ))}
                  </ul>
                </section>

                <section data-testid="guide-redirect-uri">
                  <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                    <Link2 className="h-4 w-4 text-[#7C3AED]" /> OAuth redirect URI
                  </h3>
                  <p className="mt-1 text-xs text-slate-500">Paste this into the provider's allowed redirect / callback URIs.</p>
                  <div className="mt-2 flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 p-1.5">
                    <code data-testid="guide-redirect-uri-value" className="flex-1 truncate px-2 text-xs text-slate-700">{redirectUri}</code>
                    <Button
                      data-testid="guide-copy-redirect-uri"
                      size="sm"
                      variant="outline"
                      className="shrink-0 gap-1"
                      onClick={copyRedirectUri}
                    >
                      {copied ? <><Check className="h-3.5 w-3.5 text-emerald-500" />Copied</> : <><Copy className="h-3.5 w-3.5" />Copy</>}
                    </Button>
                  </div>
                </section>

                <Separator />

                <section data-testid="guide-steps">
                  <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                    <ListChecks className="h-4 w-4 text-[#7C3AED]" /> Setup steps
                  </h3>
                  <ol className="mt-3 space-y-3">
                    {guide.steps.map((s, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-sm text-slate-600">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full aurelo-gradient text-[11px] font-bold text-white">{idx + 1}</span>
                        <span className="pt-0.5">{s}</span>
                      </li>
                    ))}
                  </ol>
                </section>

                {guide.docs && (
                  <a
                    href={guide.docs}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="guide-docs-link"
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#7C3AED] hover:underline"
                  >
                    View provider documentation <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                )}
              </div>

              <div className="mt-8 sticky bottom-0 bg-white pt-4 pb-1 border-t border-slate-100">
                <Button
                  data-testid="guide-connect-button"
                  className="w-full aurelo-gradient text-white"
                  disabled={active.status === "Connected"}
                  onClick={() => connect(active.key)}
                >
                  {active.status === "Connected"
                    ? <><Check className="h-4 w-4 mr-1" />Connected</>
                    : <>{btnLabel(active.status)} {active.name} <ArrowRight className="h-4 w-4 ml-1" /></>}
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
